import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Badge,
  Popover,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Button,
  Divider,
  Chip,
  Avatar,
  Skeleton,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  PersonAdd,
  Edit,
  Restaurant,
  Category,
  Business,
  Store,
  MarkEmailRead,
  Circle
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import apiClient from '../../../../api/client';

const MotionDiv = motion.div;

const NotificationBell = ({ sx = {} }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  
  // State management
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  
  const open = Boolean(anchorEl);

  // Fetch notification counts and recent notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      
      // Try to fetch both counts and notifications, but handle failures gracefully
      const promises = [
        apiClient.get('/api/v1/notifications/counts').catch(error => {
          console.error('Error fetching notification counts:', error);
          return { data: { unread: 0, total: 0, high_priority: 0, critical: 0, starred: 0 } };
        }),
        apiClient.get('/api/v1/notifications', { 
          params: { limit: 5, unreadOnly: false } 
        }).catch(error => {
          console.error('Error fetching notifications:', error);
          return { data: { notifications: [] } };
        })
      ];
      
      const [countsResponse, notificationsResponse] = await Promise.all(promises);
      
      setUnreadCount(countsResponse.data.unread || 0);
      setNotifications(notificationsResponse.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Set fallback values if everything fails
      setUnreadCount(0);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Monitor socket connection status
  useEffect(() => {
    const checkSocketConnection = () => {
      const socket = window.socket;
      setIsConnected(socket && socket.connected);
    };

    // Initial check
    checkSocketConnection();

    // Set up interval to check connection status
    const interval = setInterval(checkSocketConnection, 3000);

    return () => clearInterval(interval);
  }, []);

  // Socket.IO listeners for real-time updates
  useEffect(() => {
    // Listen for socket events via window events (more reliable)
    const handleNewNotification = (event) => {
      const notification = event.detail;
      console.log('🔔 New notification received:', notification);
      setNotifications(prev => [notification, ...prev.slice(0, 4)]);
      setUnreadCount(prev => prev + 1);
      
      // Show browser notification if permission granted
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(notification.title, {
          body: notification.message,
          icon: '/favicon.ico'
        });
      }
    };

    const handleCountsUpdate = (event) => {
      const counts = event.detail;
      console.log('📊 Notification counts updated:', counts);
      setUnreadCount(counts.unread || 0);
    };

    // Add event listeners
    window.addEventListener('new-notification', handleNewNotification);
    window.addEventListener('notification-counts-updated', handleCountsUpdate);

    // Also try direct socket listeners as backup
    const socket = window.socket;
    if (socket) {
      socket.on('new-notification', (notification) => {
        console.log('🔔 Direct socket notification:', notification);
        setNotifications(prev => [notification, ...prev.slice(0, 4)]);
        setUnreadCount(prev => prev + 1);
      });

      socket.on('notification-counts-updated', (counts) => {
        console.log('📊 Direct socket counts update:', counts);
        setUnreadCount(counts.unread || 0);
      });

      // Connection status listeners
      socket.on('connect', () => {
        console.log('🔌 Socket connected');
        setIsConnected(true);
      });

      socket.on('disconnect', () => {
        console.log('🔌 Socket disconnected');
        setIsConnected(false);
      });
    }

    return () => {
      // Clean up event listeners
      window.removeEventListener('new-notification', handleNewNotification);
      window.removeEventListener('notification-counts-updated', handleCountsUpdate);
      
      // Clean up socket listeners
      if (socket) {
        socket.off('new-notification');
        socket.off('notification-counts-updated');
        socket.off('connect');
        socket.off('disconnect');
      }
    };
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('📱 Notification permission:', permission);
      });
    }
  }, []);

  // Get icon for notification type
  const getNotificationIcon = (category, type) => {
    const iconProps = { fontSize: 'small' };
    
    switch (category) {
      case 'user_management':
        return type === 'USER_CREATED' ? <PersonAdd {...iconProps} /> : <Edit {...iconProps} />;
      case 'content_management':
        return type.includes('MENU') ? <Restaurant {...iconProps} /> : <Category {...iconProps} />;
      case 'vendor_management':
        return type.includes('BRANCH') ? <Store {...iconProps} /> : <Business {...iconProps} />;
      default:
        return <NotificationsIcon {...iconProps} />;
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return theme.palette.error.main;
      case 'high': return theme.palette.warning.main;
      case 'medium': return theme.palette.info.main;
      default: return theme.palette.grey[500];
    }
  };

  // Handle bell click
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    if (!open) {
      fetchNotifications();
    }
  };

  // Handle close
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Navigate to full notifications page
  const handleViewAll = () => {
    navigate('/dashboard/notifications');
    handleClose();
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await apiClient.patch(`/api/v1/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.notification_id);
    }
    
    // Navigate to relevant page if action URL exists
    if (notification.action_url) {
      navigate(notification.action_url);
      handleClose();
    }
  };

  return (
    <>
      <Tooltip title={t('notifications.title')}>
        <IconButton
          onClick={handleClick}
          color="inherit"
          aria-label={t('notifications.title')}
          sx={{
            ...sx,
            position: 'relative',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.05)',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            }
          }}
        >
          <Badge 
            badgeContent={unreadCount} 
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                animation: unreadCount > 0 ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.2)' },
                  '100%': { transform: 'scale(1)' }
                }
              }
            }}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 500,
            mt: 1,
            boxShadow: theme.shadows[10],
            border: `1px solid ${theme.palette.divider}`,
          }
        }}
      >
        <Paper sx={{ overflow: 'hidden' }}>
          {/* Simple Header */}
          <Box sx={{ 
            p: 2, 
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: alpha(theme.palette.primary.main, 0.05)
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('notifications.title')}
            </Typography>
            
            {unreadCount > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
              </Typography>
            )}
            
            {/* Connection Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Circle 
                sx={{ 
                  fontSize: 8, 
                  color: isConnected ? 'success.main' : 'error.main',
                  mr: 0.5 
                }} 
              />
              <Typography variant="caption" color="text.secondary">
                {isConnected ? 'Live Updates' : 'Disconnected'}
              </Typography>
            </Box>
          </Box>

          {/* Notifications List */}
          <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ p: 2 }}>
                {[...Array(3)].map((_, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="40%" />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  No notifications yet
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {notifications.map((notification) => (
                  <MotionDiv
                    key={notification.notification_id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        borderLeft: `4px solid ${getPriorityColor(notification.priority)}`,
                        bgcolor: notification.is_read 
                          ? 'transparent' 
                          : alpha(theme.palette.primary.main, 0.05),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.action.hover, 0.1)
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            bgcolor: notification.is_read 
                              ? theme.palette.grey[300] 
                              : theme.palette.primary.main,
                            width: 36,
                            height: 36
                          }}
                        >
                          {getNotificationIcon(notification.category, notification.type)}
                        </Avatar>
                      </ListItemIcon>

                      <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography 
                                variant="body2" 
                                component="span" // ← FIXED: Use span instead of div
                                sx={{ 
                                  fontWeight: notification.is_read ? 400 : 600,
                                  flex: 1,
                                  fontSize: '0.875rem'
                                }}
                              >
                                {notification.title}
                              </Typography>
                              
                              {!notification.is_read && (
                                <Circle sx={{ fontSize: 8, color: 'primary.main' }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box> {/* ← FIXED: Remove Typography wrapper, use Box directly */}
                              <Typography
                                component="span" // ← CRITICAL: Use span for text content
                                variant="body2"
                                sx={{ 
                                  fontSize: '0.8rem',
                                  color: 'text.secondary',
                                  display: 'block', // ← FIXED: Use block instead of webkit box
                                  mb: 0.5,
                                  lineHeight: 1.4
                                }}
                              >
                                {notification.message}
                              </Typography>
                              
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                mt: 0.5
                              }}>
                                <Typography 
                                  component="span" // ← CRITICAL: Use span instead of default p
                                  variant="caption" 
                                  sx={{ 
                                    fontSize: '0.75rem', 
                                    color: 'text.secondary' 
                                  }}
                                >
                                  {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                                </Typography>
                                
                                <Chip
                                  label={notification.priority}
                                  size="small"
                                  color={
                                    notification.priority === 'critical' ? 'error' :
                                    notification.priority === 'high' ? 'warning' :
                                    notification.priority === 'medium' ? 'info' : 'default'
                                  }
                                  sx={{ 
                                    fontSize: '0.7rem', 
                                    height: 16,
                                    textTransform: 'capitalize'
                                  }}
                                />
                              </Box>
                            </Box>
                          }
                        />
                    </ListItem>
                    
                    <Divider sx={{ ml: 7 }} />
                  </MotionDiv>
                ))}
              </List>
            )}
          </Box>

          {/* Simple Footer */}
          <Box sx={{ 
            p: 2, 
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.grey[50], 0.5)
          }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleViewAll}
                sx={{ flex: 1 }}
              >
                View All Notifications
              </Button>
              
              {unreadCount > 0 && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<MarkEmailRead fontSize="small" />}
                  onClick={() => {
                    // Mark all as read functionality
                    apiClient.patch('/api/v1/notifications/mark-all-read').then(() => {
                      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
                      setUnreadCount(0);
                    }).catch(error => {
                      console.error('Error marking all as read:', error);
                    });
                  }}
                  sx={{ flex: 1 }}
                >
                  Mark All Read
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default NotificationBell;